<template>
  <v-container>
    <v-row class="text-center" v-show="loading">
      <v-progress-circular
        class="mx-auto loader"
        indeterminate
        color="orange"
        size="75"
      ></v-progress-circular>
    </v-row>

    <v-row v-show="!loading">
      <v-col cols="12" md="4">
        <SelectProfile @select="select"></SelectProfile>
      </v-col>
      <v-col cols="12" md="5">
        <v-text-field
          v-model="term"
          label="Search"
          append-icon="mdi-magnify"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3" class="text-right" v-show="profile">
        <v-btn
          class="mx-2"
          fab
          dark
          color="orange"
          @click="dialogUpload = true"
        >
          <v-icon dark>mdi-plus</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-show="!loading">
      <v-col>
        <v-data-table :headers="headers" :items="documents" :search="term">
          <template v-slot:item.file.size="{ item }">
            {{ Number(item.file.size / 1000).toFixed(0) }} KB
          </template>
          <template v-slot:item.created="{ item }">
            {{ formatDate(item.created, "LL") }}
          </template>
          <template v-slot:item.createdBy="{ item }">
            {{ item.createdBy.firstName }} {{ item.createdBy.lastName }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  text
                  color="blue darken-4"
                  :href="item.file.path"
                  target="_blank"
                  v-on="on"
                >
                  <v-icon size="20">mdi-download</v-icon>
                </v-btn>
              </template>
              <span>Download</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  text
                  color="orange"
                  @click="showComments(item)"
                  v-on="on"
                >
                  <v-icon size="20">mdi-comment</v-icon>
                </v-btn>
              </template>
              <span>Comment</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn text color="red" @click="remove(item)" v-on="on">
                  <v-icon size="20">mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Delete</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog v-model="dialogUpload" max-width="600px">
      <v-card>
        <v-card-title class="dark-background white--text">
          Upload Document
        </v-card-title>
        <v-card-text class="mt-6">
          <SelectProfile @select="p => (profile = p)"></SelectProfile>
          <v-file-input v-model="file" label="File"></v-file-input>
          <v-textarea v-model="comment" label="Comments"></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="dialogUpload = false">Cancel</v-btn>
          <v-btn text color="orange" @click="save">
            <v-progress-circular
              v-if="saving"
              indeterminate
              color="orange"
            ></v-progress-circular>
            <span v-else>Save</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogComments" scrollable max-width="600px">
      <v-card>
        <v-card-title class="dark-background white--text">
          Comments
        </v-card-title>
        <v-card-text class="mt-6">
          <v-container>
            <v-row>
              <v-col
                cols="12"
                class="white elevation-2 mb-3"
                v-for="comment in comments"
                :key="comment.body"
              >
                <h2>
                  <span class="subtitle-2">
                    {{ comment.createdBy.firstName }}
                    {{ comment.createdBy.lastName }} |
                  </span>
                  <span class="caption">{{
                    formatDate(comment.created, "LLL")
                  }}</span>
                </h2>
                <p class="body-1 mt-3">
                  {{ comment.body }}
                </p>
              </v-col>
            </v-row>
            <v-textarea
              filled
              v-model="commentBody"
              label="Comment"
            ></v-textarea>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="dialogComments = false">Close</v-btn>
          <v-btn text color="orange" @click="addComment">
            <v-progress-circular
              v-if="saving"
              indeterminate
              color="orange"
            ></v-progress-circular>
            <span v-else>Add Comment</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="snackbar">
      {{ snackText }}
      <v-btn color="pink" text @click="snackbar = false">
        Close
      </v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import SelectProfile from "@/components/SelectProfile";

export default {
  name: "Documents",
  components: { SelectProfile },
  data() {
    return {
      comment: null,
      commentBody: null,
      document: null,
      dialogComments: false,
      dialogUpload: false,
      file: null,
      headers: [
        { text: "File Name", value: "file.name" },
        { text: "Size", value: "file.size" },
        { text: "Created By", value: "createdBy" },
        { text: "Created", value: "created" },
        {
          text: "Actions",
          value: "actions",
          align: "center",
          sortable: false,
          filterable: false
        }
      ],
      loading: true,
      profile: null,
      saving: false,
      snackbar: false,
      snackText: null,
      term: null
    };
  },
  computed: {
    ...mapGetters({
      comments: "documents/getComments",
      documents: "documents/getDocuments",
      profiles: "profiles/getProfiles",
      user: "users/getUser"
    })
  },
  methods: {
    ...mapActions({
      initUser: "users/initUser",
      listComments: "documents/listComments",
      listDocuments: "documents/listDocuments",
      createComment: "documents/createComment",
      createDocument: "documents/createDocument",
      deleteDocument: "documents/deleteDocument",
      updateDocument: "documents/updateDocument"
    }),
    async addComment() {
      await this.createComment({
        data: {
          body: this.commentBody,
          createdBy: this.user
        },
        document: this.document.id,
        profile: this.profile.id
      });
    },
    formatDate(timestamp, pattern) {
      return moment(timestamp).format(pattern);
    },
    async remove(item) {
      await this.deleteDocument({
        document: item.id,
        profile: this.profile.id
      });
    },
    async save() {
      if (!this.file) {
        this.snackbar = true;
        this.snackText = "The File field is required";
        return;
      }
      this.saving = true;
      await this.createDocument({
        comment: this.comment,
        data: {
          file: this.file,
          created: Date.now(),
          createdBy: this.user
        },
        profile: this.profile.id
      });
      this.file = this.comment = null;
      this.saving = false;
      this.dialogUpload = false;
      this.select(this.profile);
    },
    async select(profile) {
      this.loading = true;
      this.profile = profile;
      await this.listDocuments(this.profile.id);
      this.loading = false;
    },
    async showComments(item) {
      this.document = item;
      await this.listComments({
        profile: this.profile.id,
        document: this.document.id
      });
      this.dialogComments = true;
    }
  },
  async mounted() {
    await this.initUser();
    this.loading = false;
  }
};
</script>

<style scoped>
.loader {
  margin-top: 20%;
}
</style>
